import * as v from 'valibot';
import { cdCalculatorFormSchema } from './formSchema';
import type {
  CdCalculatorErrors,
  CdCalculatorFormFields,
  TermLengthUnit,
  ValidatedFormFields,
} from './types';

export function termLengthRangeMessage(unit: TermLengthUnit): string {
  return unit === 'years'
    ? 'Please enter a number between 1 and 10'
    : 'Please enter a number between 1 and 120';
}

function termLengthOutsideAllowedRangeMessage(
  termLengthUnit: TermLengthUnit,
  termLength: number,
): string | undefined {
  if (termLengthUnit === 'years') {
    if (termLength < 1 || termLength > 10) {
      return termLengthRangeMessage('years');
    }
  } else if (termLength < 1 || termLength > 120) {
    return termLengthRangeMessage('months');
  }
  return undefined;
}

type ValidationResult =
  | {
      success: true;
      output: ValidatedFormFields;
    }
  | {
      success: false;
      errors: CdCalculatorErrors;
    };

export function cdCalculatorHasBlockingErrors(errors: CdCalculatorErrors): boolean {
  return Object.values(errors).some(Boolean);
}

export function validate(formFields: CdCalculatorFormFields): ValidationResult {
  const schemaParseResult = v.safeParse(cdCalculatorFormSchema, formFields);

  if (!schemaParseResult.success) {
    const flattenedIssues = v.flatten(schemaParseResult.issues);
    const fieldErrors: CdCalculatorErrors = { ...(flattenedIssues.nested as CdCalculatorErrors) };

    if (fieldErrors.termLength) {
      fieldErrors.termLength = termLengthRangeMessage(formFields.termLengthUnit);
    } else {
      const trimmedTermInput = formFields.termLength.trim();
      const numericTermLength = trimmedTermInput === '' ? NaN : Number(trimmedTermInput);
      const termLengthOutOfRangeMessage = Number.isFinite(numericTermLength)
        ? termLengthOutsideAllowedRangeMessage(formFields.termLengthUnit, numericTermLength)
        : undefined;
      if (termLengthOutOfRangeMessage) {
        fieldErrors.termLength = termLengthOutOfRangeMessage;
      }
    }
    return {
      success: false,
      errors: fieldErrors,
    };
  }

  const validatedFormFields = schemaParseResult.output;
  const termLengthOutOfRangeMessage = termLengthOutsideAllowedRangeMessage(
    validatedFormFields.termLengthUnit,
    validatedFormFields.termLength,
  );
  if (termLengthOutOfRangeMessage) {
    return {
      success: false,
      errors: { termLength: termLengthOutOfRangeMessage },
    };
  }

  return {
    success: true,
    output: validatedFormFields,
  };
}
